<template>
  <component
    :is="showPreview ? 'BaseModalLarge' : 'BaseModal'"
    :heading="t('register.invoice.issue_header', { number: order?.number })"
    useForm
    :loading="orderLoading"
    :label="order?.state"
    @submit="onSubmit"
    @close="$emit('close')"
    testId="issue-invoice"
  >
    <Flex direction="column">
      <BaseAlert
        color="warning"
        :text="showPreview ? t('register.invoice.issue_alert') : t('register.invoice.issue_alert_no_preview')"
      />
      <Flex direction="column">
        <Flex :wrap="true">
          <div :class="$style.flex1">
            <Details
              :invoicedAt="order.invoicedAt"
              v-model:dueInDays="invoiceData.dueInDays"
              :showDueDate="!isFiskalySignIntegrated && !isFullyPaid"
            />
          </div>
          <div :class="$style.flex1">
            <Customer
              :orderId="order.id"
              :customer="invoiceData.customer"
              :medical="order.medical"
              :showError="showCustomerError"
              @updated="handleCustomerUpdated"
            />
          </div>
        </Flex>
      </Flex>
      <EmailInvoice
        v-model:email="emailInvoiceData.email"
        v-model:subject="emailInvoiceData.subject"
        v-model:content="emailInvoiceData.content"
        v-model:sendInvoice="sendInvoice"
      />
      <Preview
        v-if="showPreview"
        v-model:isLoaded="isPreviewLoaded"
        :orderId="order.id"
      />
    </Flex>
    <template #footer>
      <BaseButton
        color="inverted"
        @click="$emit('close')"
      >
        {{ t('global.actions.cancel') }}
      </BaseButton>
      <BaseButton
        submitForm
        :disabled="orderLoading || (showPreview && !isPreviewLoaded) || isSubmitting"
        v-test="'issue-invoice-submit'"
      >
        {{ submitText }}
      </BaseButton>
    </template>
  </component>
</template>

<script
  setup
  lang="ts"
>
import { useQuery } from '@vue/apollo-composable';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { scrollPage } from '@/helpers/scroll';
import { flash, modal } from '@/helpers/ui';
import { segment } from '@/segment';
import { useUserStore } from '@/stores/user';
import type { Customer as CustomerType } from '@/types';
import { GET_ORDER } from '../graphql';
import Customer from './Customer.vue';
import Details from './Details.vue';
import EmailInvoice from './EmailInvoice.vue';
import Preview from './Preview.vue';
import { useSubmit } from './submit';

const props = defineProps<{
  orderId: string;
}>();

const emit = defineEmits(['close', 'submit']);

// French fiskaly-integrated companies get the frozen facture (ADR 0012), which has
// no due date and no live preview — the preview can never match the signed document,
// and the due date isn't a concept on it. Mirrors the OrdersController#preview guard.
const { hasFeatureFlag } = useUserStore();
const isFiskalySignIntegrated =
  hasFeatureFlag('fiskaly-sign') && hasFeatureFlag('fiskaly-sign-enabled');
const showPreview = !isFiskalySignIntegrated;

const {
  loading: orderLoading,
  onResult,
  result,
} = useQuery(GET_ORDER, {
  id: props.orderId,
});

const order = computed(() => result.value?.order);
const isFullyPaid = computed(
  () => !!order.value && !order.value.amountOutstanding,
);
const isPreviewLoaded = ref(false);
const isSubmitting = ref(false);
const showCustomerError = ref(false);

const { t } = useI18n();
const { sendInvoice, invoiceData, emailInvoiceData, submit } = useSubmit();
invoiceData.orderId = props.orderId;

const submitText = computed(() =>
  sendInvoice.value
    ? t('global.actions.issue_invoice_and_send')
    : t('global.actions.issue_invoice'),
);

onResult(({ data }) => {
  const orderData = data?.order;
  if (!orderData) {
    return;
  }

  invoiceData.orderId = props.orderId;
  invoiceData.customer = orderData.customer;
  emailInvoiceData.email = orderData.customer?.email || '';
  emailInvoiceData.subject = orderData.invoiceEmailSubjectTemplate || '';
});

const customerDataMissing = computed(() => {
  return (
    !invoiceData.customer ||
    !invoiceData.customer.address ||
    !invoiceData.customer.zipcode ||
    !invoiceData.customer.city
  );
});

const handleCustomerUpdated = (customer: CustomerType) => {
  showCustomerError.value = false;
  invoiceData.customer = customer;
  emailInvoiceData.email = customer?.email || '';
};

const onSubmit = () => {
  if (customerDataMissing.value) {
    scrollPage({ position: 'top' });
    showCustomerError.value = true;
    return;
  }

  modal('confirmation', {
    message: t('register.invoice.issue_confirmation_message'),
  }).then(() => {
    isSubmitting.value = true;
    segment.track('register.sales.issue_invoice.confirmed', {
      send_invoice: sendInvoice.value,
      due_days: invoiceData.dueInDays,
    });
    submit()
      .then((result) => {
        if (result) {
          flash(t('global.flash.invoice_issued'));
          emit('submit');
        } else {
          flash(t('global.flash.invoice_issue_error'));
        }
        isSubmitting.value = false;
      })
      .catch(() => {
        flash(t('global.flash.invoice_issue_error'));
        isSubmitting.value = false;
      });
  });
};
</script>

<style
  module
  lang="scss"
>
.flex1 {
  min-width: 300px;
  flex: 1;
}
</style>
